import { useEffect, useRef, useState } from 'react';
import { connectClientSocket, disconnectClientSocket, type ClientSocket } from './socket.js';

/**
 * Protocole de reconnexion obligatoire : docs/05-websocket.md, section 9.
 *
 * - au montage : la page a deja charge son etat par REST ;
 * - 'disconnect' : connected passe a false, la page affiche le bandeau ;
 * - 'connect' apres une coupure : resync() rejoue la requete REST complete.
 *
 * Aucune page ne reimplemente ce protocole : elle abonne ses ecouteurs
 * metier au socket retourne et rend le bandeau si connected est false.
 */
export function useRealtimeSync(
  sessionToken: string | null,
  resync: () => void,
): { socket: ClientSocket | null; connected: boolean } {
  const [socket, setSocket] = useState<ClientSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const everConnected = useRef(false);
  const resyncRef = useRef(resync);
  resyncRef.current = resync;

  useEffect(() => {
    if (!sessionToken) return;
    everConnected.current = false;
    const s = connectClientSocket(sessionToken);
    setSocket(s);

    const onConnect = () => {
      if (everConnected.current) resyncRef.current();
      everConnected.current = true;
      setConnected(true);
    };
    const onDisconnect = () => setConnected(false);

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);

    return () => {
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      disconnectClientSocket();
      setSocket(null);
      setConnected(false);
    };
  }, [sessionToken]);

  return { socket, connected };
}
